import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import AuthClientStore from '../../../common/services/auth-client.service';
import { openVcamSession, VcamControl, VcamPose, VcamSessionInfo, VcamState } from './vcam';

const API_URL = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';

/**
 * The desktop end of the phone camera: opens a session, joins its room and
 * hands every pose and button press to the studio. Lives in the studio dialog
 * (not in the panel showing the QR code), so closing the panel keeps the
 * phone linked.
 */
export function useVcamLink(opts: {
    productUuid?: string;
    onPose: (pose: VcamPose) => void;
    onControl: (control: VcamControl) => void;
}) {
    const [session, setSession] = useState<VcamSessionInfo | null>(null);
    const [opening, setOpening] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [phoneLinked, setPhoneLinked] = useState(false);
    const socketRef = useRef<Socket | null>(null);
    // The studio's handlers change every render; the socket reads the latest.
    const handlers = useRef(opts);
    handlers.current = opts;

    const close = useCallback(() => {
        socketRef.current?.disconnect();
        socketRef.current = null;
        setSession(null);
        setPhoneLinked(false);
    }, []);

    const open = useCallback(async () => {
        if (socketRef.current || opening) return;
        setOpening(true);
        setError(null);
        try {
            const s = await openVcamSession(handlers.current.productUuid);
            const socket = io(`${API_URL}/vcam`, {
                auth: { token: AuthClientStore.getAccessToken(), code: s.code },
                transports: ['websocket', 'polling'],
            });
            socket.on('vcam:peer', (p: any) => { if (p?.role === 'phone') setPhoneLinked(!!p.joined); });
            socket.on('vcam:pose', (pose: VcamPose) => { setPhoneLinked(true); handlers.current.onPose(pose); });
            socket.on('vcam:control', (c: VcamControl) => handlers.current.onControl(c));
            socket.on('connect_error', (e) => setError(e?.message || 'connect_error'));
            socketRef.current = socket;
            setSession(s);
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setOpening(false);
        }
    }, [opening]);

    const sendState = useCallback((state: VcamState) => {
        socketRef.current?.emit('vcam:state', state);
    }, []);

    useEffect(() => () => { socketRef.current?.disconnect(); }, []);

    return { session, opening, error, phoneLinked, open, close, sendState };
}
